import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertTriangle,
  ShieldCheck,
  Flag,
  Tag,
  IndianRupee,
  ChevronRight,
  ChevronDown,
  MapPin,
  Sparkles,
} from "lucide-react";
import { getActiveProjectGraph } from "../../data/sampleGraph";
import { explain, narrate, type WhyPath } from "../../lib/whyEngine";
import type { GraphNode, NodeKind } from "../../types/ontology";
import { NODE_PALETTE } from "../../types/ontology.generated";
import { cls } from "../../lib/cls";

interface WhyPanelProps {
  focusedNodeId: string;
  onFocusChange?: (nodeId: string) => void;
  pickableNodeKinds?: GraphNode["kind"][];
  onShowOnMap?: ((nodeId: string) => void) | undefined;
}

// Default pickable kinds + their group order (alerts first — that's
// always the highest-value Why target).
const DEFAULT_PICKABLE_KINDS: NodeKind[] = ["Alert", "Approval", "Milestone", "SKU", "BudgetLine"];

const GROUP_META: Record<NodeKind, { label: string; icon: JSX.Element; intro: string }> = {
  Alert:      { label: "Open alerts",        icon: <AlertTriangle className="w-3 h-3" />, intro: "Things firing right now" },
  Approval:   { label: "Approvals",          icon: <ShieldCheck className="w-3 h-3" />,   intro: "Decisions waiting on people" },
  Milestone:  { label: "Milestones",         icon: <Flag className="w-3 h-3" />,          intro: "Dates the project depends on" },
  SKU:        { label: "Items being watched", icon: <Tag className="w-3 h-3" />,           intro: "SKUs touched by your rules" },
  BudgetLine: { label: "Budget lines",       icon: <IndianRupee className="w-3 h-3" />,   intro: "Per-room money the brain tracks" },
  // Fallback labels for any kind that isn't grouped above:
  Project:    { label: "Project",            icon: <Tag className="w-3 h-3" />,           intro: "" },
  Client:     { label: "Client",             icon: <Tag className="w-3 h-3" />,           intro: "" },
  Intent:     { label: "Intent",             icon: <Tag className="w-3 h-3" />,           intro: "" },
  Style:      { label: "Style",              icon: <Tag className="w-3 h-3" />,           intro: "" },
  Room:       { label: "Rooms",              icon: <Tag className="w-3 h-3" />,           intro: "" },
  Zone:       { label: "Zones",              icon: <Tag className="w-3 h-3" />,           intro: "" },
  Placement:  { label: "Placements",         icon: <Tag className="w-3 h-3" />,           intro: "" },
  Material:   { label: "Materials",          icon: <Tag className="w-3 h-3" />,           intro: "" },
  Vendor:     { label: "Vendors",            icon: <Tag className="w-3 h-3" />,           intro: "" },
  Rule:       { label: "Rules",              icon: <Tag className="w-3 h-3" />,           intro: "" },
  MemoryCard: { label: "Memory",             icon: <Tag className="w-3 h-3" />,           intro: "" },
  FloorPlan:  { label: "Floor plans",        icon: <Tag className="w-3 h-3" />,           intro: "" },
  CatalogImport: { label: "Catalog imports", icon: <Tag className="w-3 h-3" />, intro: "Where each SKU came from" },
  Inspiration:   { label: "Inspirations",    icon: <Tag className="w-3 h-3" />, intro: "Photos driving the design" },
  TimelineEvent: { label: "Timeline events", icon: <Tag className="w-3 h-3" />, intro: "Every human edit + agent fire, in sync" },
};

export function WhyPanel({ focusedNodeId, onFocusChange, pickableNodeKinds, onShowOnMap }: WhyPanelProps): JSX.Element {
  const focused = useMemo(
    () => getActiveProjectGraph().nodes.find((n) => n.id === focusedNodeId) ?? null,
    [focusedNodeId],
  );
  const paths = useMemo(() => explain(getActiveProjectGraph(), focusedNodeId), [focusedNodeId]);
  const [pathIdx, setPathIdx] = useState(0);
  const currentPath: WhyPath | null = paths[pathIdx] ?? null;

  const kinds = pickableNodeKinds && pickableNodeKinds.length > 0 ? pickableNodeKinds : DEFAULT_PICKABLE_KINDS;
  const groups = useMemo(() => {
    return kinds.map((k) => ({
      kind: k,
      nodes: getActiveProjectGraph().nodes.filter((n) => n.kind === k),
    })).filter((g) => g.nodes.length > 0);
  }, [kinds]);

  return (
    <div className="panel p-5 sm:p-6 space-y-4">
      <header>
        <div className="eyebrow">Why-engine</div>
        <div className="display-3 mt-1 text-ink-950">What caused this?</div>
        <p className="mt-1.5 text-[12px] text-ink-500 leading-snug">
          Pick anything red, slipping, or pending. The brain traces it back to the rule that fired
          — in plain English.
        </p>
      </header>

      {focused && currentPath ? (
        <FocusedDetail
          focused={focused}
          paths={paths}
          pathIdx={pathIdx}
          onChangePath={setPathIdx}
          currentPath={currentPath}
          onShowOnMap={onShowOnMap ?? null}
          onPickAnother={() => onFocusChange?.("")}
        />
      ) : (
        <Picker
          groups={groups}
          focusedId={focusedNodeId}
          onPick={(id) => onFocusChange?.(id)}
        />
      )}
    </div>
  );
}

/* ─── Picker — grouped, alerts-first ─── */

function Picker({
  groups,
  focusedId,
  onPick,
}: {
  groups: { kind: NodeKind; nodes: GraphNode[] }[];
  focusedId: string;
  onPick: (id: string) => void;
}): JSX.Element {
  return (
    <div className="space-y-3">
      {groups.map(({ kind, nodes }) => (
        <PickerGroup key={kind} kind={kind} nodes={nodes} focusedId={focusedId} onPick={onPick} />
      ))}
    </div>
  );
}

function PickerGroup({
  kind, nodes, focusedId, onPick,
}: {
  kind: NodeKind;
  nodes: GraphNode[];
  focusedId: string;
  onPick: (id: string) => void;
}): JSX.Element {
  const [open, setOpen] = useState(kind === "Alert" || kind === "Approval");
  const meta = GROUP_META[kind];
  const palette = NODE_PALETTE[kind];
  return (
    <section>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-left mb-1.5 group"
      >
        <div className="flex items-center gap-1.5">
          <span className="w-4 h-4 rounded-md grid place-items-center text-cream" style={{ background: palette.hex }}>
            {meta.icon}
          </span>
          <span className="text-[11px] uppercase tracking-wider text-ink-700 font-semibold">{meta.label}</span>
          <span className="pill bg-cream-dark border border-border text-ink-500 tabular text-[9px]">{nodes.length}</span>
        </div>
        {open ? <ChevronDown className="w-3 h-3 text-ink-400" /> : <ChevronRight className="w-3 h-3 text-ink-400" />}
      </button>
      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <div className="flex flex-wrap gap-1.5">
              {nodes.map((n) => (
                <PickerChip key={n.id} node={n} active={n.id === focusedId} onPick={() => onPick(n.id)} />
              ))}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </section>
  );
}

function PickerChip({ node, active, onPick }: { node: GraphNode; active: boolean; onPick: () => void }): JSX.Element {
  const palette = NODE_PALETTE[node.kind];
  const severity = node.kind === "Alert" ? String(node.attrs["severity"] ?? "info") : null;
  const state = node.kind === "Approval" ? String(node.attrs["state"] ?? "") : null;
  return (
    <button
      onClick={onPick}
      className={cls(
        "pill border transition-all",
        active
          ? "bg-ink-950 text-cream border-ink-950"
          : "bg-cream-dark border-border text-ink-950 hover:border-foyr/50",
      )}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: palette.hex }} />
      <span className="truncate max-w-[200px]">{node.label}</span>
      {severity ? (
        <span className={cls(
          "text-[9px] px-1 rounded",
          severity === "block" ? "bg-foyr text-cream" :
          severity === "warn"  ? "bg-foyr/15 text-foyr-deep" :
                                 "bg-sea/15 text-sea",
        )}>
          {severity}
        </span>
      ) : null}
      {state ? (
        <span className="text-[9px] px-1 rounded bg-mulberry/10 text-mulberry">{state}</span>
      ) : null}
    </button>
  );
}

/* ─── Focused detail — picked node's causal chain ─── */

function FocusedDetail({
  focused,
  paths,
  pathIdx,
  onChangePath,
  currentPath,
  onShowOnMap,
  onPickAnother,
}: {
  focused: GraphNode;
  paths: WhyPath[];
  pathIdx: number;
  onChangePath: (i: number) => void;
  currentPath: WhyPath;
  onShowOnMap: ((id: string) => void) | null;
  onPickAnother: () => void;
}): JSX.Element {
  const palette = NODE_PALETTE[focused.kind];
  const steps = currentPath.steps;
  // Step 0 is the focused node itself; cause chain starts at step 1.
  const causeSteps = steps.slice(1);
  return (
    <motion.div
      key={`${focused.id}-${pathIdx}`}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.22 }}
      className="space-y-3"
    >
      {/* Focused header */}
      <div className="flex items-start gap-2.5 p-3 rounded-xl bg-cream-deeper border border-border">
        <span className="w-7 h-7 rounded-lg grid place-items-center text-cream shrink-0" style={{ background: palette.hex }}>
          <Sparkles className="w-3.5 h-3.5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider text-ink-500">{focused.kind}</div>
          <div className="text-sm font-semibold text-ink-950 leading-tight">{focused.label}</div>
          <div className="text-[11px] text-ink-500 mt-0.5">
            {paths.length === 1 ? "1 reason found" : `${paths.length} reasons · showing the top one`}
          </div>
        </div>
        <button onClick={onPickAnother} className="text-[11px] text-ink-500 hover:text-ink-950 underline-offset-2 hover:underline shrink-0">
          Pick another
        </button>
      </div>

      {/* Numbered cause chain — designer-friendly */}
      {causeSteps.length === 0 ? (
        <div className="text-[12px] text-ink-500 italic px-1">
          No upstream cause in the graph for this node — it's a leaf.
        </div>
      ) : (
        <ol className="space-y-2">
          {causeSteps.map((step, i) => (
            <li key={`${step.node.id}-${i}`} className="flex items-start gap-2.5">
              <span className="w-5 h-5 rounded-full bg-ink-950 text-cream grid place-items-center text-[10px] font-semibold shrink-0 mt-0.5">
                {i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wider text-accent">
                  {prettyReason(step.reason)}
                </div>
                <div className="text-[13px] text-ink-950 font-medium leading-tight">{step.node.label}</div>
                <div className="text-[10px] text-ink-500 leading-tight">{step.node.kind.toLowerCase()}</div>
              </div>
              <span className="shrink-0 w-3 h-3 rounded-full border" style={{ background: NODE_PALETTE[step.node.kind].soft, borderColor: NODE_PALETTE[step.node.kind].hex }} />
            </li>
          ))}
        </ol>
      )}

      {/* Plain-English narrative */}
      {causeSteps.length > 0 ? (
        <div className="p-3 rounded-xl bg-foyr/5 border border-foyr/20">
          <div className="eyebrow text-foyr-deep mb-1">In plain English</div>
          <div className="text-[12.5px] text-ink-950 leading-relaxed whitespace-pre-line">
            {narrate(currentPath)}
          </div>
        </div>
      ) : null}

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-1.5">
        {onShowOnMap ? (
          <button onClick={() => onShowOnMap(focused.id)} className="btn btn-outline !text-[11px] !py-1.5">
            <MapPin className="w-3 h-3" /> Show on project map
          </button>
        ) : null}
        {paths.length > 1 ? (
          <div className="flex items-center gap-1 ml-auto">
            <span className="text-[10px] text-ink-500 uppercase tracking-wider">Other reasons</span>
            {paths.map((_, i) => (
              <button
                key={i}
                onClick={() => onChangePath(i)}
                className={cls(
                  "w-6 h-6 rounded-full text-[10px] font-mono",
                  i === pathIdx ? "bg-foyr text-cream" : "bg-cream-dark border border-border text-ink-700 hover:border-foyr/40",
                )}
              >
                {i + 1}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </motion.div>
  );
}

function prettyReason(raw: string): string {
  // The raw reason comes from `reasonFor(edge)` in whyEngine.ts.
  // Already mostly designer-friendly — capitalise + trim.
  if (!raw) return "step";
  const t = raw.trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
}
